import * as pdfjsLib from 'pdfjs-dist';

class PDFViewer {
    constructor() {
        this.activeViewers = new Map();
        this.pdfjsDistVersion = '2.16.105';
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@' + this.pdfjsDistVersion + '/build/pdf.worker.min.js';
    }

    init() {
        this.bindModalEvents();
    }

    bindModalEvents() {
        // Listen for modal triggers (buttons/links that open modals)
        document.addEventListener('click', (e) => {
            let element = e.target;

            if (!element.matches('[data-modal]') && !element.matches('[href^="#modal-"]')) {
                let parentElement = element.closest('[data-modal]');
                if (!parentElement) {
                    parentElement = element.closest('[href^="#modal-"]');
                }
                if (parentElement) {
                    element = parentElement;
                }
            }

            if (element && (element.matches('[data-modal]') || element.matches('[href^="#modal-"]'))) {
                // handle kidsmobile special case
                if (document.querySelector('.js-sweepstake-modal')) {
                    let sweepstakeContainer = document.querySelector('.js-sweepstake');
                    if (sweepstakeContainer) {
                        sweepstakeContainer.dataset.openerTarget = element.dataset.href || '';
                    }
                }

                let contentTarget = element.dataset.hasOwnProperty('modal') ? element.dataset.modal : element.getAttribute('href').substr(1);

                setTimeout(() => {
                    this.checkForActiveModals(contentTarget);
                }, 300);
            }

            if (element && element.classList.contains('js-modal-close')) {
                const modalContents = document.querySelectorAll('[data-modal-content]');

                modalContents.forEach((modal) => {
                    this.cleanupPDFViewersInModal(modal);
                });                
            }
        });
    }

    /**
     * Check for active modals and initialize PDF viewers
     * @param {*} modalId 
     */
    checkForActiveModals(modalId) {
        const activeModals = document.querySelectorAll('.modal.is-active[data-modal-content="' + modalId + '"]');
        
        activeModals.forEach(modal => {
            this.initPDFViewersInModal(modal);
        });
    }

    /**
     * Initialize PDF viewers in modal
     * @param {*} modal 
     */
    initPDFViewersInModal(modal) {
        if (!modal.classList.contains('is-active')) {
            return;
        }
        
        const containers = modal.querySelectorAll('.pdf-viewer-container[data-pdf-url]');
        
        containers.forEach(container => {
            if (container.dataset.pdfInitialized !== 'true') {
                this.initPDFViewer(container);
            } else {
                console.log('PDF already initialized in container:', container);
            }
        });
    }

    /**
     * Cleanup PDF viewers in modal
     * @param {*} modal 
     */
    cleanupPDFViewersInModal(modal) {
        const containers = modal.querySelectorAll('.pdf-viewer-container[data-pdf-url]');
        containers.forEach(container => {
            this.cleanupPDFViewer(container);
        });
    }

    /**
     * Initialize PDF viewer
     * @param {*} container 
     */
    initPDFViewer(container) {
        if (container.dataset.pdfInitialized === 'true') {
            return;
        }

        const pdfUrl = container.dataset.pdfUrl;

        if (!pdfUrl) {
            console.error('No PDF URL provided');
            return;
        }

        // Generate unique ID for this viewer instance
        const viewerId = 'pdf-viewer-' + Math.random().toString(36).substr(2, 9);
        container.dataset.viewerId = viewerId;
        container.dataset.pdfInitialized = 'true';

        // Create viewer state object
        const viewerState = {
            container: container,
            pagesContainer: container.querySelector('#pdf-pages-container'),
            pdfDoc: null,
            scale: 1.0,
            pageIsRendering: false
        };

        if (!viewerState.pagesContainer) {
            this.showError(container);
            return;
        }

        // Store viewer state
        this.activeViewers.set(viewerId, viewerState);

        // Show loading state
        this.showLoading(container);

        // Load PDF
        const loadingTask = pdfjsLib.getDocument({
            url: pdfUrl,
            cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@' + this.pdfjsDistVersion + '/cmaps/',
            cMapPacked: true,
        });

        loadingTask.promise.then((pdfDoc) => {
            viewerState.pdfDoc = pdfDoc;
            
            // Render all pages
            return this.renderAllPages(viewerId);
        }).then(() => {
            // Hide loading state
            this.hideLoading(container);
        }).catch((error) => {
            this.showError(container);
        });
    }

    renderAllPages(viewerId) {
        const viewerState = this.activeViewers.get(viewerId);

        if (!viewerState || !viewerState.pdfDoc) {
            return Promise.resolve();
        }

        viewerState.pageIsRendering = true;
        const { pdfDoc, pagesContainer, scale } = viewerState;

        // Calculate responsive scale based on container width
        const containerWidth = pagesContainer.parentElement.clientWidth;
        const maxWidth = containerWidth - 40; // Subtract padding


        // Clear existing content
        pagesContainer.innerHTML = '';

        // Create promises for all pages
        const pagePromises = [];
        
        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
            const pagePromise = pdfDoc.getPage(pageNum).then((page) => {
                // Get initial viewport to calculate scale
                const initialViewport = page.getViewport({ scale: 1.0 });

                // Calculate scale to fit container width
                const scale = Math.min(maxWidth / initialViewport.width, 1.0); // Max scale of 1.0
                const viewport = page.getViewport({ scale });

                // Create canvas for this page
                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                canvas.style.display = 'block';

                const ctx = canvas.getContext('2d');

                // Add page number overlay
                const pageWrapper = document.createElement('div');
                pageWrapper.style.position = 'relative';
                pageWrapper.style.display = 'inline-block';
                pageWrapper.appendChild(canvas);

                // const pageLabel = document.createElement('div');
                // pageLabel.textContent = `Seite ${pageNum}`;
                // pageLabel.style.position = 'absolute';
                // pageLabel.style.top = '5px';
                // pageLabel.style.right = '5px';
                // pageLabel.style.background = 'rgba(0,0,0,0.7)';
                // pageLabel.style.color = 'white';
                // pageLabel.style.padding = '2px 6px';
                // pageLabel.style.fontSize = '12px';
                // pageLabel.style.borderRadius = '3px';
                // pageWrapper.appendChild(pageLabel);

                pagesContainer.appendChild(pageWrapper);

                // Render page
                const renderContext = {
                    canvasContext: ctx,
                    viewport: viewport
                };

                return page.render(renderContext).promise.then(() => {
                    return { pageNum, success: true };
                });
            }).catch((error) => {
                return { pageNum, success: false, error };
            });
            
            pagePromises.push(pagePromise);
        }

        // Wait for all pages to complete
        return Promise.all(pagePromises).then((results) => {
            viewerState.pageIsRendering = false;
            const successCount = results.filter(r => r.success).length;
        }).catch((error) => {
            viewerState.pageIsRendering = false;
            throw error;
        });
    }

    /**
     * Show loading state icon
     * @param {*} container 
     */
    showLoading(container) {
        const loading = container.querySelector('#pdf-loading');
        const pagesContainer = container.querySelector('#pdf-pages-container');

        if (loading) {
            loading.style.display = 'block';
        }

        if (pagesContainer) {
            pagesContainer.style.display = 'none';
        }
    }

    /**
     * Hide loading state icon
     * @param {*} container 
     */
    hideLoading(container) {
        const loading = container.querySelector('#pdf-loading');
        const pagesContainer = container.querySelector('#pdf-pages-container');

        if (loading) { 
            loading.style.display = 'none';
        }

        if (pagesContainer) { 
            pagesContainer.style.display = 'block';
        }
    }

    /**
     * Show error message
     * @param {*} container 
     */
    showError(container) {
        const error = container.querySelector('#pdf-error');
        const loading = container.querySelector('#pdf-loading');

        if (error) {
            error.style.display = 'block';
        }
        if (loading) {
            loading.style.display = 'none';
        }
    }

    /**
     * Cleanup PDF viewer
     * @param {*} container 
     */
    cleanupPDFViewer(container) {
        const viewerId = container.dataset.viewerId;
        if (!viewerId) return;

        // Remove from active viewers
        this.activeViewers.delete(viewerId);

        // Reset initialization flag
        container.dataset.pdfInitialized = 'false';
        delete container.dataset.viewerId;
    }

    /**
     * Destroy PDF viewer
     */
    destroy() {      
        // Clean up all viewers
        this.activeViewers.clear();
    }
}

export default new PDFViewer();